import type { Club, ClubPlanTier, SubscriptionTier } from '@court-sync/shared';
import {
  clubPublicBaseUrl,
  clubPublicPath,
  type ClubPublicUrlOptions,
} from '@court-sync/shared';
import { getClubHostMode, type ClubHostMode } from './clubHost';

export type ClubPathFields = Pick<Club, 'slug' | 'subdomain' | 'subdomainEnabled'> & {
  orgTier?: ClubPlanTier | SubscriptionTier;
};

const DEFAULT_ORG_TIER: ClubPlanTier | SubscriptionTier = 'professional';

function resolveOrgTier(club: ClubPathFields): ClubPlanTier | SubscriptionTier {
  return club.orgTier ?? DEFAULT_ORG_TIER;
}

/** App origin for path-mode URLs when the user is on a branded subdomain (Firebase continueUrl fallback). */
function resolveWebAppOriginForPathFallback(): string {
  if (typeof window === 'undefined') return 'https://app.courtsync.io';
  const { hostname, port } = window.location;
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';
    return `http://localhost${portSuffix}`;
  }
  if (hostname.endsWith('.dev.courtsync.io') || hostname === 'dev.courtsync.io') {
    return 'https://app.dev.courtsync.io';
  }
  return 'https://app.courtsync.io';
}

/** Relative club-scoped path for the current host mode. */
export function buildClubPath(
  club: ClubPathFields,
  subpath: string,
  hostMode: ClubHostMode = getClubHostMode()
): string {
  return clubPublicPath(club, resolveOrgTier(club), subpath, hostMode);
}

/** Absolute public URL for a club path (emails, share links, embed link-out). */
export function buildClubAbsoluteUrl(
  club: ClubPathFields,
  subpath = '',
  options?: ClubPublicUrlOptions,
  hostMode: ClubHostMode = 'path'
): string {
  return clubPublicBaseUrl(club, resolveOrgTier(club), subpath, options, hostMode);
}

/** Login path for the current host (subdomain or path mode). */
export function buildClubLoginPath(
  club: ClubPathFields,
  returnPath?: string,
  hostMode: ClubHostMode = getClubHostMode()
): string {
  const loginPath = buildClubPath(club, '/login', hostMode);
  if (!returnPath) return loginPath;
  const sep = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

function appendReturnUrlToAbsolute(url: string, returnPath?: string): string {
  if (!returnPath) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Forgot-password path for the current host. */
export function buildClubForgotPasswordPath(
  club: ClubPathFields,
  returnPath?: string,
  hostMode: ClubHostMode = getClubHostMode()
): string {
  const path = buildClubPath(club, '/forgot-password', hostMode);
  if (!returnPath) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Register path for the current host. */
export function buildClubRegisterPath(
  club: ClubPathFields,
  returnPath?: string,
  hostMode: ClubHostMode = getClubHostMode()
): string {
  const path = buildClubPath(club, '/register', hostMode);
  if (!returnPath) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/**
 * Path-mode auth URL for embed link-out (always opens on app origin, not iframe).
 */
export function buildClubAuthLinkOutUrl(
  club: ClubPathFields,
  subpath: string,
  returnPath?: string
): string {
  const path = clubPublicPath(
    { ...club, subdomainEnabled: false },
    resolveOrgTier(club),
    subpath,
    'path'
  );
  return appendReturnUrlToAbsolute(`${resolveWebAppOriginForPathFallback()}${path}`, returnPath);
}

/**
 * Firebase password-reset continue URLs. Subdomain hosts may be unauthorized in Firebase;
 * always use path-mode app origin (always authorized).
 */
export function buildClubPasswordResetContinueUrls(
  club: ClubPathFields,
  returnPath?: string
): { primary: string; fallback: string } {
  const pathReset = clubPublicPath(
    { ...club, subdomainEnabled: false },
    resolveOrgTier(club),
    '/reset-password',
    'path'
  );
  const pathModeUrl = appendReturnUrlToAbsolute(
    `${resolveWebAppOriginForPathFallback()}${pathReset}`,
    returnPath
  );
  return { primary: pathModeUrl, fallback: pathModeUrl };
}
